import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from 'constants/apiConfig';
import { parseError } from 'hooks/shared/error';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectGroupGuid } from 'slices/commonSelectors';
import { keys as incidentActionKeys } from 'components/IAP/hooks/incidentActionHooks';
import { keys as actionKeys } from 'components/IAP/hooks/actionHooks';
import { keys as rosterKeys } from 'hooks/incident/useRoster';
import { incidentRosterKeys } from 'components/IAP/hooks/incidentRoster/incidentRoster.queryKeys';
import { filesOrFoldersKeys } from 'components/FileManagement/hooks/useGetAllFiles';
import type { ActionAssigneeKind } from 'components/IAP/ICSConstants';
import type { ActionChecklistItem } from 'components/IAP/actionChecklistModel';
import type { ActionTicketFieldValues } from 'components/IAP/actionTicket/actionTicketModel';
import type { GeoLocation } from 'components/GeoLocationComponent/buildGeoLocationFromGeoJson';
import { planApplyToast } from './planLibraryLabels';
import { useUscgFocRequirementsFlag } from 'utils/featureFlags';
import {
  getUscgFocPlan,
  isUscgFocPlanId,
  USCG_FOC_DEMO_APPLY_MESSAGE,
} from 'components/MapPage/CorporateViewTabs/PlanLibraryTab/uscgFocPlanning';

export { planApplyToast, planContentLabel } from './planLibraryLabels';

export const PLAN_LIBRARY_PRIORITIES = [
  'Low',
  'Normal',
  'High',
  'Critical',
] as const;

export type PlanLibraryPriority = (typeof PLAN_LIBRARY_PRIORITIES)[number];

export type PlanLibraryTask = ActionTicketFieldValues & {
  id: string;
  name: string;
  description: string;
  status: string;
  priority: PlanLibraryPriority;
  classification: string;
  checklist: ActionChecklistItem[];
  assignee_kind: ActionAssigneeKind | null;
  assignee_id: string | null;
  poc: string | null;
  briefed_poc: boolean;
  start_date: Date | string | null;
  target_date: Date | string | null;
  geolocation: GeoLocation | null;
  symbology: string | null;
  aor_id: string | null;
  aor_ids?: string[];
  depends_on: string[];
};

export type PlanLibraryFormFamily = 'uscg' | 'fema';

export type PlanLibraryForm = {
  id: string;
  formName: string;
  label: string;
  widgetName: string;
  family: PlanLibraryFormFamily;
  payload: {
    fields: Record<string, unknown>;
  };
};

export type PlanLibraryFile = {
  fileId: string;
  name: string;
};

export type PlanLibraryLayerKind = 'internal' | 'external';

export type PlanLibraryLayer = {
  kind: PlanLibraryLayerKind;
  id: string;
  name: string;
};

export type PlanLibraryIncidentTags = {
  useForIncidents: boolean;
  incidentCategories: string[];
  aorIds: string[];
};

export type PlanLibraryPlan = PlanLibraryIncidentTags & {
  id: string;
  groupGuid: string;
  name: string;
  description: string | null;
  tasks: PlanLibraryTask[];
  forms: PlanLibraryForm[];
  files: PlanLibraryFile[];
  rosterTemplateIds: string[];
  layers: PlanLibraryLayer[];
  /** HWCG Responsible Party company this plan is meant for, when tagged. */
  responsiblePartyCompany: string | null;
  /** Filled roster template applied when creating an incident from this plan. */
  filledRosterTemplateId: string | null;
  /**
   * Hidden incident the plan's ICS forms are authored in; null until a form is
   * added.
   */
  templateIncidentId: string | null;
  archived: boolean;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
};

export type PlanLibraryWritePayload = Partial<PlanLibraryIncidentTags> & {
  name: string;
  description?: string | null;
  tasks?: Array<
    Omit<PlanLibraryTask, 'id'> & {
      id?: string;
    }
  >;
  forms?: PlanLibraryForm[];
  files?: PlanLibraryFile[];
  rosterTemplateIds?: string[];
  layers?: PlanLibraryLayer[];
  responsiblePartyCompany?: string | null;
  filledRosterTemplateId?: string | null;
};

export type PlanLibraryApplyResult = {
  createdActionIds: string[];
  createdCount: number;
  createdFormIds?: string[];
  createdFormCount?: number;
  createdFileCount?: number;
  rosterSeatsAdded?: number;
  suggestedLayers?: PlanLibraryLayer[];
};

/** Fills list fields an older API response may omit. */
export function normalizePlanLibraryPlan(
  plan: PlanLibraryPlan
): PlanLibraryPlan {
  return {
    ...plan,
    tasks: plan.tasks ?? [],
    forms: plan.forms ?? [],
    files: plan.files ?? [],
    rosterTemplateIds: plan.rosterTemplateIds ?? [],
    layers: plan.layers ?? [],
    useForIncidents: plan.useForIncidents ?? false,
    incidentCategories: plan.incidentCategories ?? [],
    aorIds: plan.aorIds ?? [],
    responsiblePartyCompany: plan.responsiblePartyCompany ?? null,
    filledRosterTemplateId: plan.filledRosterTemplateId ?? null,
    templateIncidentId: plan.templateIncidentId ?? null,
  };
}

function planLibraryError(error: unknown): string {
  const parsed = parseError(error);
  if (parsed) return parsed;
  const data = (error as { response?: { data?: { error?: unknown } } })
    ?.response?.data?.error;
  if (typeof data === 'string' && data.trim()) return data;
  return 'Something went wrong';
}

export const planLibraryKeys = {
  all: ['plan-library'] as const,
  list: (groupGuid: string) =>
    [...planLibraryKeys.all, 'list', groupGuid] as const,
};

export function usePlanLibraryPlans(options?: { enabled?: boolean }) {
  const groupGuid = useSelector(selectGroupGuid);
  const uscgFocEnabled = useUscgFocRequirementsFlag();
  return useQuery({
    queryKey: [
      ...planLibraryKeys.list(groupGuid ?? ''),
      uscgFocEnabled ? 'uscg-foc' : '',
    ],
    enabled: Boolean(groupGuid) && options?.enabled !== false,
    queryFn: async () => {
      const response = await axios.get<PlanLibraryPlan[]>(
        config.apiGateway.plan_library,
        { params: { groupGuid } }
      );
      const plans = (response.data ?? []).map(normalizePlanLibraryPlan);
      if (!uscgFocEnabled || !groupGuid) return plans;
      const fixture = getUscgFocPlan(groupGuid);
      if (plans.some((plan) => plan.id === fixture.id)) return plans;
      return [...plans, fixture];
    },
  });
}

export function useCreatePlanLibraryPlan() {
  const groupGuid = useSelector(selectGroupGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: PlanLibraryWritePayload) => {
      const response = await axios.post<PlanLibraryPlan>(
        config.apiGateway.plan_library,
        { ...payload, groupGuid }
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: planLibraryKeys.list(groupGuid ?? ''),
      });
    },
    onError: (error) => toast.error(planLibraryError(error)),
  });
}

export function useUpdatePlanLibraryPlan() {
  const groupGuid = useSelector(selectGroupGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...payload
    }: PlanLibraryWritePayload & { id: string }) => {
      const response = await axios.put<PlanLibraryPlan>(
        `${config.apiGateway.plan_library}/${id}`,
        { ...payload, groupGuid }
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: planLibraryKeys.list(groupGuid ?? ''),
      });
    },
    onError: (error) => toast.error(planLibraryError(error)),
  });
}

export function useArchivePlanLibraryPlan() {
  const groupGuid = useSelector(selectGroupGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await axios.delete(`${config.apiGateway.plan_library}/${id}`, {
        params: { groupGuid },
      });
      return id;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: planLibraryKeys.list(groupGuid ?? ''),
      });
    },
    onError: (error) => toast.error(planLibraryError(error)),
  });
}

/** The plan's template incident id, creating the incident the first time. */
export function useEnsurePlanTemplateIncident() {
  const groupGuid = useSelector(selectGroupGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      planId: string;
      formFamily?: PlanLibraryFormFamily;
    }) => {
      const response = await axios.post<{ templateIncidentId: string }>(
        `${config.apiGateway.plan_library}/${input.planId}/template-incident`,
        { groupGuid, formFamily: input.formFamily }
      );
      return response.data.templateIncidentId;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: planLibraryKeys.list(groupGuid ?? ''),
      });
    },
    onError: (error) => toast.error(planLibraryError(error)),
  });
}

export function useApplyPlanLibraryPlan() {
  const groupGuid = useSelector(selectGroupGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      incidentId: string;
      positionId?: string;
    }) => {
      if (isUscgFocPlanId(input.id)) {
        return {
          createdActionIds: [],
          createdCount: 0,
          demonstration: true as const,
        };
      }
      const response = await axios.post<PlanLibraryApplyResult>(
        `${config.apiGateway.plan_library}/${input.id}/apply`,
        {
          groupGuid,
          incidentId: input.incidentId,
          ...(input.positionId ? { positionId: input.positionId } : {}),
        }
      );
      return response.data;
    },
    onSuccess: (result, input) => {
      if ('demonstration' in result && result.demonstration) {
        toast.info(USCG_FOC_DEMO_APPLY_MESSAGE);
        return;
      }
      void queryClient.invalidateQueries({
        queryKey: [rosterKeys.incidentRoster, input.incidentId],
      });
      void queryClient.invalidateQueries({
        queryKey: incidentRosterKeys.draftByIncident(input.incidentId),
      });
      void queryClient.invalidateQueries({
        queryKey: filesOrFoldersKeys.allFiles(input.incidentId),
        exact: false,
      });
      void queryClient.invalidateQueries({ queryKey: incidentActionKeys.base });
      void queryClient.invalidateQueries({ queryKey: actionKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['actions', 'mine'] });
      void queryClient.invalidateQueries({ queryKey: ['icsFormVersions'] });
      void queryClient.invalidateQueries({ queryKey: ['incidentInfo'] });
      toast.success(planApplyToast(result));
    },
    onError: (error) => toast.error(planLibraryError(error)),
  });
}
